import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { isMobilePhoneBrowser } from '../utils/deviceDetection';

// "Sign in" on the home page behaves exactly like "Continue" on the previous home page
// (components/JusticeMission.js):
//  - asks for a reload if this copy of the app was loaded more than a day ago
//  - phones go to the mobile experience (/m/cases with a valid session, else /m/login)
//  - everyone else goes to /login

const log = (...args) => console.log('[LandingPage]', ...args);

const APP_LOADED_AT_KEY = 'app_loaded_at_ms';
const RELOAD_MS = 1 * 24 * 60 * 60 * 1000;
// Set just before the "get latest version" reload so the freshly loaded page
// carries on to the sign-in destination without a second click.
const RESUME_SIGN_IN_KEY = 'resume_sign_in_after_reload';

function hasValidSession() {
  try {
    const token = localStorage.getItem('jwtToken');
    if (!token) return false;
    const decoded = jwtDecode(token);
    return Boolean(decoded?.exp) && decoded.exp > Date.now() / 1000;
  } catch {
    return false;
  }
}

export default function useAppEntry() {
  const navigate = useNavigate();
  const [updateRequired, setUpdateRequired] = useState(false);

  // remember when this copy of the app was first loaded
  useEffect(() => {
    try {
      const existing = localStorage.getItem(APP_LOADED_AT_KEY);
      if (!existing || Number.isNaN(Number(existing))) {
        localStorage.setItem(APP_LOADED_AT_KEY, String(Date.now()));
        log('recorded app load time');
      }
    } catch {
      log('local storage unavailable; skipping app load time');
    }
  }, []);

  const checkUpdateRequired = () => {
    try {
      const loadedAt = Number(localStorage.getItem(APP_LOADED_AT_KEY));
      if (!Number.isNaN(loadedAt) && loadedAt > 0 && Date.now() - loadedAt > RELOAD_MS) {
        log('this copy of the app is more than a day old; asking for a reload');
        setUpdateRequired(true);
        return true;
      }
    } catch {
      // storage unavailable: carry on
    }
    return false;
  };

  const goToSignIn = () => {
    if (isMobilePhoneBrowser()) {
      const dest = hasValidSession() ? '/m/cases' : '/m/login';
      log(`sign in (phone) -> ${dest}`);
      navigate(dest);
    } else {
      log('sign in (desktop) -> /login');
      navigate('/login');
    }
  };

  const signIn = (e) => {
    if (e) e.preventDefault();
    if (checkUpdateRequired()) return;
    goToSignIn();
  };

  // After the "get latest version" reload, continue to sign-in automatically.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(RESUME_SIGN_IN_KEY)) {
        sessionStorage.removeItem(RESUME_SIGN_IN_KEY);
        log('resuming sign in after reload');
        goToSignIn();
      }
    } catch {
      // storage unavailable: user can click Sign in again
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLatestVersion = () => {
    try {
      localStorage.setItem(APP_LOADED_AT_KEY, String(Date.now()));
      sessionStorage.setItem(RESUME_SIGN_IN_KEY, '1');
    } catch {
      // ignore
    }
    log('reloading to get the latest version');
    window.location.reload();
  };

  return { signIn, updateRequired, getLatestVersion };
}
